import React, { useMemo } from 'react';
import Icon from '../../../components/AppIcon';

const DeliveryAnalyticsPanel = ({ emails, statistics }) => {
  const analyticsData = useMemo(() => {
    if (!emails || emails?.length === 0) return null;

    // Group by status
    const statusDistribution = emails?.reduce((acc, email) => {
      const status = email?.notification_status || 'unknown';
      acc[status] = (acc?.[status] || 0) + 1;
      return acc;
    }, {});

    // Group by date
    const dateDistribution = emails?.reduce((acc, email) => {
      const date = new Date(email?.created_at)?.toLocaleDateString();
      acc[date] = (acc?.[date] || 0) + 1;
      return acc;
    }, {});

    // Calculate failure patterns
    const failedEmails = emails?.filter(e => e?.notification_status === 'failed') || [];
    const failureReasons = failedEmails?.reduce((acc, email) => {
      const reason = email?.error_message || 'Unknown error';
      acc[reason] = (acc?.[reason] || 0) + 1;
      return acc;
    }, {});

    return {
      statusDistribution,
      dateDistribution,
      failureReasons
    };
  }, [emails]);

  if (!analyticsData) {
    return (
      <div className="text-center py-12">
        <Icon name="BarChart3" size={48} className="mx-auto text-neutral-300 mb-4" />
        <p className="text-neutral-600">No analytics data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Success Rate Overview */}
      <div className="bg-white rounded-lg border border-neutral-200 p-6">
        <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center gap-2">
          <Icon name="TrendingUp" size={20} />
          Success Rate Overview
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <p className="text-sm text-neutral-600 mb-1">Success Rate</p>
            <p className="text-3xl font-bold text-green-600">{statistics?.successRate || 0}%</p>
          </div>
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-neutral-600 mb-1">Total Delivered</p>
            <p className="text-3xl font-bold text-blue-600">{statistics?.delivered || 0}</p>
          </div>
          <div className="text-center p-4 bg-red-50 rounded-lg">
            <p className="text-sm text-neutral-600 mb-1">Total Failed</p>
            <p className="text-3xl font-bold text-red-600">{statistics?.failed || 0}</p>
          </div>
        </div>
      </div>
      {/* Status Distribution */}
      <div className="bg-white rounded-lg border border-neutral-200 p-6">
        <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center gap-2">
          <Icon name="PieChart" size={20} />
          Status Distribution
        </h3>
        <div className="space-y-3">
          {Object?.entries(analyticsData?.statusDistribution || {})?.map(([status, count]) => {
            const percentage = ((count / emails?.length) * 100)?.toFixed(1);
            const colorClass = {
              delivered: 'bg-green-500',
              sent: 'bg-blue-500',
              pending: 'bg-yellow-500',
              failed: 'bg-red-500'
            }?.[status] || 'bg-gray-500';

            return (
              <div key={status}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-neutral-700 capitalize">{status}</span>
                  <span className="text-sm text-neutral-600">{count} ({percentage}%)</span>
                </div>
                <div className="w-full bg-neutral-200 rounded-full h-2">
                  <div
                    className={`${colorClass} h-2 rounded-full transition-all duration-300`}
                    style={{ width: `${percentage}%` }}
                  ></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* Common Failure Reasons */}
      {Object?.keys(analyticsData?.failureReasons || {})?.length > 0 && (
        <div className="bg-white rounded-lg border border-neutral-200 p-6">
          <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center gap-2">
            <Icon name="AlertTriangle" size={20} />
            Common Failure Reasons
          </h3>
          <div className="space-y-2">
            {Object?.entries(analyticsData?.failureReasons || {})
              ?.sort((a, b) => b?.[1] - a?.[1])
              ?.slice(0, 5)
              ?.map(([reason, count]) => (
                <div key={reason} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                  <span className="text-sm text-neutral-700 flex-1">{reason}</span>
                  <span className="text-sm font-medium text-red-600">{count} occurrences</span>
                </div>
              ))}
          </div>
        </div>
      )}
      {/* Delivery Time Analysis */}
      <div className="bg-white rounded-lg border border-neutral-200 p-6">
        <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center gap-2">
          <Icon name="Clock" size={20} />
          Delivery Time Analysis
        </h3>
        <div className="text-center p-6 bg-purple-50 rounded-lg">
          <p className="text-sm text-neutral-600 mb-2">Average Delivery Time</p>
          <p className="text-4xl font-bold text-purple-600">{statistics?.avgDeliveryTime || 0}s</p>
          <p className="text-sm text-neutral-500 mt-2">Based on successfully delivered emails</p>
        </div>
      </div>
    </div>
  );
};

export default DeliveryAnalyticsPanel;